/**
 * [Insider+] Subscriber welcome page — hosts the iframe at /welcome (or the
 * last-visited deep path the child reports via IFRAME_ROUTE_CHANGED).
 */

import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import { INSIDER_PLUS_BASE_URL } from '../security/config';
import InsiderPlusIframeShell from '../components/InsiderPlus/InsiderPlusIframeShell';
import {
  clearInsiderPlusPath,
  getStoredInsiderPlusPath,
  isSafeInsiderPlusPath,
  storeInsiderPlusPath,
} from '../util/helpers/insiderPlusPathHelper';
import useInsiderPlusIframe from '../util/hooks/useInsiderPlusIframe';
import useGtmHook from '../util/hooks/useGtmHook';

const DEFAULT_WELCOME_PATH = '/welcome';

const NPS_FIRED_KEY = 'nps_welcome_fired';

const InsiderPlusWelcomePage = () => {
  const history = useHistory();
  const { gtmCustomEvent } = useGtmHook();

  // Resolve from sessionStorage each call so location.key re-seeds land back
  // on the deep page (reload) or on Welcome (Header link, which clears storage
  // before navigating).
  const buildIframeSrc = useCallback(
    () =>
      `${INSIDER_PLUS_BASE_URL}${
        getStoredInsiderPlusPath() ?? DEFAULT_WELCOME_PATH
      }`,
    [],
  );

  // Same-origin targets (e.g. an iframe CTA into /insider-plus-membership) go
  // through react-router so the parent tree doesn't full-reload and the
  // matching header tab highlights correctly. Clear the stored deep path so
  // Welcome's path-restore doesn't undo the navigation on remount. External
  // targets (e.g. Discourse forum) fall back to window.location.href and keep
  // the stored path so the user lands back on their last iframe page on
  // return.
  const onNavigateUrl = useCallback(
    (url) => {
      try {
        const target = new URL(url, window.location.origin);
        if (target.origin === window.location.origin) {
          clearInsiderPlusPath();
          history.push(target.pathname + target.search + target.hash);
          return;
        }
      } catch {
        // Malformed URL — fall through to full-page navigation
      }
      window.location.href = url;
    },
    [history],
  );

  // IFRAME_ROUTE_CHANGED: the child reports its current deep path on every
  // route change; we persist it so a parent reload re-seeds the iframe on the
  // page the user was last viewing rather than always /welcome.
  //
  // NPS_ELIGIBLE: the child emits this once per session when an active
  // subscriber lands on the welcome hub. Hotjar refuses to render surveys
  // inside an iframe, so we fire the NPS trigger here in the top-level window
  // via the parent's GTM/Hotjar. Guarded once per parent session.
  const onIframeMessage = useCallback(
    (event) => {
      if (event.data?.type === 'IFRAME_ROUTE_CHANGED') {
        const path = event.data?.path;
        if (isSafeInsiderPlusPath(path)) {
          storeInsiderPlusPath(path);
        }
        return;
      }
      if (event.data?.type === 'NPS_ELIGIBLE') {
        if (sessionStorage.getItem(NPS_FIRED_KEY)) return;
        gtmCustomEvent('nps_welcome');
        sessionStorage.setItem(NPS_FIRED_KEY, '1');
      }
    },
    [gtmCustomEvent],
  );

  const {
    iframeRef,
    iframeSrc,
    iframeKey,
    isRecovering,
    authFailed,
    isRetrying,
    handleIframeLoad,
    handleRetry,
  } = useInsiderPlusIframe({
    buildIframeSrc,
    onNavigateUrl,
    onIframeMessage,
  });

  return (
    <InsiderPlusIframeShell
      iframeRef={iframeRef}
      iframeSrc={iframeSrc}
      iframeKey={iframeKey}
      title="Insider Plus Welcome"
      dataCy="container-insider-plus-welcome-page"
      isRecovering={isRecovering}
      authFailed={authFailed}
      isRetrying={isRetrying}
      onIframeLoad={handleIframeLoad}
      onRetry={handleRetry}
    />
  );
};

export default InsiderPlusWelcomePage;
